import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Contact page — real backend POST /api/contact (Resend-backed). Falls back
 * to mailto: only if the server call fails outright, so guests without a
 * configured mail client can still reach us.
 */
export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !subject.trim() || !body.trim()) {
      return toast.error("Please fill in all fields");
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        body: body.trim(),
      });
      toast.success(`Message sent · we usually reply within 24 hours (ref ${data.id.slice(-6)})`);
      setName(""); setEmail(""); setSubject(""); setBody("");
    } catch (err) {
      // Network / server hiccup → fall back to the classic mailto handoff so
      // the user never gets stranded.
      const detail = err.response?.data?.detail;
      if (detail && err.response?.status < 500) {
        toast.error(detail);
      } else {
        toast.error("Server unreachable — opening your mail client instead.");
        const params = new URLSearchParams({
          subject: `[EasyStay] ${subject}`,
          body: `From: ${name} <${email}>\n\n${body}`,
        });
        window.location.href = `mailto:support@easystayzm.com?${params}`;
      }
    } finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8" data-testid="contact-page">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
        <MessageSquare className="h-3.5 w-3.5" /> Support
      </div>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
        Contact EasyStay
      </h1>
      <p className="mt-2 text-slate-600">
        Guests, hosts, and partners — we&apos;re here to help. Most queries are answered within 24 hours.
      </p>

      <div className="mt-8 grid gap-8 md:grid-cols-5">
        {/* Quick contacts */}
        <div className="md:col-span-2 space-y-4">
          <div className="rounded-3xl bg-white p-5 card-shadow">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
              <Mail className="h-3.5 w-3.5" /> Email
            </div>
            <div className="mt-2 space-y-1 text-sm">
              <a href="mailto:support@easystayzm.com" className="block text-slate-900 hover:text-copper">support@easystayzm.com</a>
              <a href="mailto:hello@easystayzm.com" className="block text-slate-900 hover:text-copper">hello@easystayzm.com</a>
              <a href="mailto:privacy@easystayzm.com" className="block text-slate-900 hover:text-copper">privacy@easystayzm.com</a>
            </div>
          </div>
          <div className="rounded-3xl bg-white p-5 card-shadow">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
              <MapPin className="h-3.5 w-3.5" /> Head office
            </div>
            <p className="mt-2 text-sm text-slate-700">
              EasyStay Zambia<br />
              Lusaka, Zambia<br />
              <a href="https://easystayzm.com" className="text-copper hover:underline">easystayzm.com</a>
            </p>
          </div>
          <div className="rounded-3xl bg-white p-5 card-shadow">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
              <Phone className="h-3.5 w-3.5" /> WhatsApp / SMS
            </div>
            <p className="mt-2 text-sm text-slate-700">
              Reply to any EasyStay SMS — booking confirmations and digital keys accept follow-up messages. Text <b>HELP</b> to hear back from support.
            </p>
          </div>
        </div>

        {/* Message form */}
        <form onSubmit={submit} className="md:col-span-3 rounded-3xl bg-white p-6 card-shadow" data-testid="contact-form">
          <h2 className="font-display text-lg font-bold text-slate-900">Send us a message</h2>
          <p className="mt-1 text-xs text-slate-500">
            We&apos;ll reply to the email you enter below.
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <input
              value={name} onChange={(e) => setName(e.target.value.slice(0, 120))}
              placeholder="Your name" data-testid="contact-name"
              className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
            />
            <input
              value={email} onChange={(e) => setEmail(e.target.value.slice(0, 200))}
              placeholder="Your email" type="email" data-testid="contact-email"
              className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
            />
          </div>
          <input
            value={subject} onChange={(e) => setSubject(e.target.value.slice(0, 200))}
            placeholder="Subject" data-testid="contact-subject"
            className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
          />
          <textarea
            value={body} onChange={(e) => setBody(e.target.value.slice(0, 2000))}
            rows={6} placeholder="How can we help?" data-testid="contact-body"
            className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
          />
          <div className="mt-2 text-right text-xs text-slate-400">{body.length}/2000</div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="btn-primary mt-2 text-sm"
          >
            <Send className="mr-1 inline h-4 w-4" /> {sending ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 text-sm">
        <Link to="/terms/guest" className="btn-outline">Guest Terms</Link>
        <Link to="/terms/host" className="btn-outline">Host Agreement</Link>
        <Link to="/privacy" className="btn-outline">Privacy Policy</Link>
      </div>
    </div>
  );
}
